import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { resolveNewsApiLang } from '@formosa/shared/utils/newsLang';
import { useTranslation } from '../../i18n/LocaleContext';
import SettingsSubPageShell from '../../components/settings/SettingsSubPageShell';

const PAGE_SIZE = 20;

export default function NewsListPage() {
  const { locale, t } = useTranslation();
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const data = await memberApi.newsPageQuery({
          pageNo: page,
          pageSize: PAGE_SIZE,
          lang: resolveNewsApiLang(locale),
        });
        if (cancelled) {
          return;
        }
        setRecords(data?.records || []);
        setTotalPages(data?.pages || 1);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || t('news.loadFailed'));
          setRecords([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [locale, page, t]);

  return (
    <SettingsSubPageShell title={t('news.title')}>
      {loading ? <p className="news-page-status">{t('common.loading')}</p> : null}
      {error ? <p className="news-page-status news-page-status--error">{error}</p> : null}
      {!loading && !error && records.length === 0 ? (
        <p className="news-page-status">{t('news.empty')}</p>
      ) : null}

      <ul className="news-list">
        {records.map((item) => (
          <li key={item.id}>
            <Link to={`/news/${item.id}`} className="news-list__item">
              <div className="news-list__title">{item.title}</div>
              <div className="news-list__time">{formatDateTime(item.newsTime)}</div>
            </Link>
          </li>
        ))}
      </ul>

      {totalPages > 1 ? (
        <div className="news-list-pagination">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            {t('news.prevPage')}
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('news.nextPage')}
          </button>
        </div>
      ) : null}
    </SettingsSubPageShell>
  );
}
